import type { ComponentType } from "react";
import {
  Users,
  UsersRound,
  Workflow,
  Building2,
  Briefcase,
  Megaphone,
  Layers,
  Rocket,
  BookOpen,
  GitCompare,
  GitBranch,
  HelpCircle,
  ShieldCheck,
} from "lucide-react";

export type NavMenuItem = {
  label: string;
  description: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  external?: boolean;
};

export type NavMenuGroup = {
  title?: string;
  items: NavMenuItem[];
};

export type NavMenuCta = {
  eyebrow: string;
  title: string;
  body: string;
  href: string;
};

export type NavigationItem = {
  name: string;
  link: string;
  mobileLink: string;
  dropdown?: {
    groups: NavMenuGroup[];
    cta?: NavMenuCta;
  };
};

export const navigationItems: NavigationItem[] = [
  {
    name: "Workers",
    link: "/workers",
    mobileLink: "/workers",
  },
  {
    name: "Teams",
    link: "/teams",
    mobileLink: "/teams",
  },
  {
    name: "Products",
    link: "/products/digital-workers",
    mobileLink: "/products/digital-workers",
    dropdown: {
      groups: [
        {
          title: "Workforce",
          items: [
            {
              label: "Digital Workers",
              description:
                "Named workers across Sales, Marketing, Ops, Platform.",
              href: "/products/digital-workers",
              icon: Users,
            },
            {
              label: "Teams",
              description:
                "Pre-built workforces — Lead Gen, Content, Reception, Campaigns.",
              href: "/products/teams",
              icon: UsersRound,
            },
            {
              label: "Departments",
              description:
                "Full functions — Marketing, Revenue, or a custom build.",
              href: "/products/departments",
              icon: Building2,
            },
          ],
        },
        {
          title: "Building blocks",
          items: [
            {
              label: "Workflows",
              description:
                "Cron and event-triggered automation across your stack.",
              href: "/products/workflows",
              icon: Workflow,
            },
            {
              label: "Pipelines",
              description:
                "Parameterised templates — run the same work on every input.",
              href: "/products/pipelines",
              icon: GitBranch,
            },
          ],
        },
      ],
      cta: {
        eyebrow: "See it run",
        title: "Walk through the platform",
        body: "From hire to revenue in four steps — with real screenshots.",
        href: "/products/digital-workers",
      },
    },
  },
  {
    name: "Solutions",
    link: "/for/sales",
    mobileLink: "/for/sales",
    dropdown: {
      groups: [
        {
          title: "By function",
          items: [
            {
              label: "For Sales Teams",
              description:
                "Outbound on autopilot. Pipeline that doesn’t need a BDR hire.",
              href: "/for/sales",
              icon: Briefcase,
            },
            {
              label: "For Marketing",
              description:
                "Content, ads, and DMs running 24/7 across every channel.",
              href: "/for/marketing",
              icon: Megaphone,
            },
            {
              label: "For Operations",
              description:
                "Workflows, KPIs, and reports — without an ops manager.",
              href: "/for/operations",
              icon: Layers,
            },
            {
              label: "For Agencies",
              description: "Run more accounts with the same headcount.",
              href: "/for/agencies",
              icon: Rocket,
            },
          ],
        },
      ],
      cta: {
        eyebrow: "See use cases",
        title: "Workers shipping in production",
        body: "Real workflows running across companies right now.",
        href: "/#case-studies",
      },
    },
  },
  {
    name: "Compare",
    link: "/compare/artisan",
    mobileLink: "/compare/artisan",
    dropdown: {
      groups: [
        {
          title: "vs. the AI labor market",
          items: [
            {
              label: "vs. Artisan",
              description: "One AI BDR or a whole workforce?",
              href: "/compare/artisan",
              icon: GitCompare,
            },
            {
              label: "vs. 11x",
              description: "A few agents or a 100-worker org chart?",
              href: "/compare/11x",
              icon: GitCompare,
            },
            {
              label: "vs. Lindy",
              description: "Personal assistants or a department workforce?",
              href: "/compare/lindy",
              icon: GitCompare,
            },
            {
              label: "vs. Glean",
              description: "Search answers or get the work done?",
              href: "/compare/glean",
              icon: GitCompare,
            },
          ],
        },
      ],
      cta: {
        eyebrow: "See it in production",
        title: "Walk through the platform",
        body: "Real screenshots of workers shipping right now.",
        href: "/products/digital-workers",
      },
    },
  },
  {
    name: "Resources",
    link: "/#case-studies",
    mobileLink: "/#case-studies",
    dropdown: {
      groups: [
        {
          items: [
            {
              label: "Use Cases",
              description: "Case studies of workers in production.",
              href: "/#case-studies",
              icon: BookOpen,
            },
            {
              label: "How It Works",
              description: "The four-step engagement model end-to-end.",
              href: "/#how-i-work",
              icon: Workflow,
            },
            {
              label: "vs. The Old Way",
              description: "What changes when AI workers run your operations.",
              href: "/#comparison",
              icon: GitCompare,
            },
            {
              label: "What Poly Is / Isn’t",
              description: "Positioning for honest expectations.",
              href: "/#identity",
              icon: HelpCircle,
            },
            {
              label: "Privacy Policy",
              description: "How we handle data and access.",
              href: "/privacy-policy",
              icon: ShieldCheck,
            },
          ],
        },
      ],
    },
  },
  {
    name: "How to hire",
    link: "/#how-to-hire",
    mobileLink: "/#how-to-hire",
  },
  {
    name: "Book a call",
    link: "/#footer",
    mobileLink: "/#footer",
  },
];
